import { type ReactNode } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Boxes, Home, LayoutDashboard, LogOut, Settings } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Link } from '@/components/ui/link';
import { Chip } from '@/components/ui/chip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

export function AppShell({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // AppShell only ever renders behind RequireAuth, so user is always set here.
  const displayName = [user!.firstName, user!.lastName].filter(Boolean).join(' ') || user!.email;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const setLang = (lang: 'tr' | 'en') => {
    void i18n.changeLanguage(lang);
  };
  const isTr = i18n.language?.startsWith('tr');

  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-[16rem_1fr]">
      <aside className="border-r bg-card md:sticky md:top-0 md:h-screen">
        <div className="flex h-16 items-center border-b px-6">
          <RouterLink to="/" className="flex items-center gap-2 font-semibold">
            <Boxes className="h-5 w-5 text-primary" />
            <span>{t('app.name')}</span>
            <span className="text-xs text-muted-foreground">{t('app.tagline')}</span>
          </RouterLink>
        </div>
        <nav className="space-y-1 p-3">
          <Link to="/" end intent="standalone" className="flex items-center gap-3 px-3 text-sm">
            <LayoutDashboard className="h-4 w-4 shrink-0" /> {t('nav.dashboard')}
          </Link>
          <Link to="/items" intent="standalone" className="flex items-center gap-3 px-3 text-sm">
            <Boxes className="h-4 w-4 shrink-0" /> {t('nav.items')}
          </Link>
          <Link to="/locations" intent="standalone" className="flex items-center gap-3 px-3 text-sm">
            <Home className="h-4 w-4 shrink-0" /> {t('nav.locations')}
          </Link>
          <Link to="/settings" intent="standalone" className="flex items-center gap-3 px-3 text-sm">
            <Settings className="h-4 w-4 shrink-0" /> {t('nav.settings')}
          </Link>
        </nav>
      </aside>
      <div className="flex min-h-screen flex-col">
        <header className="flex h-16 items-center justify-between border-b bg-card px-6">
          <RouterLink
            to="/profile"
            className="text-sm text-muted-foreground hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-control"
            aria-label={t('profile.viewProfile')}
            title={t('profile.viewProfile')}
          >
            {displayName}
          </RouterLink>
          <div className="flex items-center gap-3">
            <div role="radiogroup" aria-label={t('settings.language')} className="flex items-center gap-1 rounded-pill bg-surface-2 p-1">
              <Chip role="radio" aria-checked={isTr} selected={isTr} onClick={() => setLang('tr')} className="h-6 px-2.5 text-xs">
                TR
              </Chip>
              <Chip role="radio" aria-checked={!isTr} selected={!isTr} onClick={() => setLang('en')} className="h-6 px-2.5 text-xs">
                EN
              </Chip>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm">
                  <LogOut className="mr-2 h-4 w-4" /> {t('auth.logout')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('auth.logoutTitle')}</AlertDialogTitle>
                  <AlertDialogDescription>{t('auth.logoutConfirm')}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel asChild>
                    <Button variant="secondary">{t('common.cancel')}</Button>
                  </AlertDialogCancel>
                  <AlertDialogAction asChild>
                    <Button variant="primary" onClick={handleLogout}>{t('auth.logout')}</Button>
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </header>
        <main className="flex-1 p-6">{children}</main>
      </div>
    </div>
  );
}
